import React from "react";
import {css} from "@panda/css";

import {WideContent} from "@pg-design/grid-module";
import {Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {ColumnList} from "../../atoms/ColumnList";
import {ColumnListItem} from "../../atoms/ColumnListItem";
import {TimeStamp} from "../../atoms/TimeStamp";
import {TwoColumnGridHolder, TwoColumnGridMain} from "../../atoms/TwoColumnGrid";
import {useGetOfferListQuery} from "../../offer/api/get_offer_list";
import {OfferBox} from "../../offer/components/offer_box/OfferBox";
import {OfferModal} from "../../offer/components/offer_modal/OfferModal";
import {showOfferModal} from "../../offer/state/offer_modals_slice";
import {offerBoxTracking} from "../../offer/tracking/offer_box_gtm_events";
import {IOfferListOffer} from "../../offer/types/IOfferListOffer";
import {OfferModalActiveTab} from "../../offer/types/OfferModalActiveTab";
import {IOfferModalName} from "../../offer/types/OfferModalName";
import {getDetailPageRelatedOffersPayload} from "../../offer/utils/get_detail_page_related_offers_payload";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {getRequestStateFromRtkQuery} from "../../store/utils/get_request_state_from_rtk_query";
import {useGTMEcommerceOfferListViewTracking} from "../../tracking/hooks/use_gtm_ecommerce_offer_list_view_tracking";
import {FilterPresets, useUserPreferencesFilters} from "../../user/hooks/use_user_preferences_filters";
import {IVariables} from "../../variables/reducers/variables_reducer";
import {VendorTrustCertificate} from "../../vendor/components/VendorTrustCertificate";
import {IPropertyDetail} from "../types/IPropertyDetail";
import {IPropertyDetailOffer} from "../types/IPropertyDetailOffer";
import {PropertyDetailDescription} from "./PropertyDetailDescription";
import {PropertyDetailDetails} from "./PropertyDetailDetails";
import {PropertyDetailLocation} from "./PropertyDetailLocation";
import {PropertyNavBar} from "./PropertyNavBar";

interface IProps {
    offer: IPropertyDetailOffer;
    property: IPropertyDetail;
    relatedOffers: IOfferListOffer[];
    variables: IVariables | null;
    changeModalTab: (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection) => void;
    showPriceAndContactButtons: boolean;
    shouldMapBeRendered?: boolean;
}

const modalName: IOfferModalName = "mainOfferModal";

export const PropertyDetailLimited: React.FC<IProps> = React.memo((props) => {
    const {property, offer, variables, relatedOffers} = props;

    const dispatch = useAppDispatch();
    const modalOpened = useAppSelector((state) => state.offerModals[modalName].isOpen);
    const userPreferencesFilters = useUserPreferencesFilters(FilterPresets.WithoutRegion);

    const {
        data: relatedOffersResponse,
        isSuccess,
        isUninitialized,
        isLoading,
        isFetching,
        isError
    } = useGetOfferListQuery(
        getDetailPageRelatedOffersPayload({
            page: 1,
            offer
        }),
        {skip: !modalOpened}
    );

    const showVendorTrustCertificate = !!offer.vendor.awards_count;
    const publishDate = !offer.configuration.hide_publish_date && offer.configuration.publish_date ? offer.configuration.publish_date : null;

    const closestInvestmentsTitle = "Najbliższe inwestycje";
    const {containerId} = useGTMEcommerceOfferListViewTracking({
        offers: relatedOffers,
        listId: "najbliższe_inwestycje",
        listName: closestInvestmentsTitle,
        useVisibilityTracking: true
    });

    return (
        <>
            <PropertyNavBar
                className={navBarWrapperStyle}
                navBarContentStyle={navBarContentStyle}
                offer={property.offer}
                onButtonClick={props.changeModalTab}
            />

            {relatedOffers.length > 0 && (
                <>
                    <WideContent className={wideContentStyle}>
                        <Text as="h2" className={headlineStyle} variant="headline_4">
                            {closestInvestmentsTitle}
                        </Text>

                        <ColumnList id={containerId} data-id={containerId}>
                            {relatedOffers.map((offer, index) => (
                                <ColumnListItem key={offer.id}>
                                    <OfferBox
                                        offer={offer}
                                        onShowOfferDetailsBtnClick={() => {
                                            dispatch(showOfferModal({modalName, offerId: offer.id, offer}));
                                        }}
                                        index={index}
                                        trackingMeta={{
                                            listName: closestInvestmentsTitle,
                                            listId: "najbliższe_inwestycje",
                                            source: "additional",
                                            tileEvents: {
                                                imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
                                                buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
                                                linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
                                                logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
                                            }
                                        }}
                                    />
                                </ColumnListItem>
                            ))}
                        </ColumnList>
                    </WideContent>
                    <OfferModal
                        modalName={modalName}
                        offersQuery={{
                            isInitialized: !isUninitialized,
                            isSuccess,
                            isLoading: isLoading || isFetching,
                            isError,
                            requestState: getRequestStateFromRtkQuery({isSuccess, isLoading, isFetching, isError}),
                            data: relatedOffersResponse ? relatedOffersResponse.results : [],
                            count: relatedOffersResponse ? relatedOffersResponse.count : 0,
                            page: relatedOffersResponse ? relatedOffersResponse.page : 1,
                            pageSize: relatedOffersResponse ? relatedOffersResponse.page_size : 3
                        }}
                        userPreferencePropertyFilters={userPreferencesFilters}
                    />
                </>
            )}

            <TwoColumnGridHolder>
                <TwoColumnGridMain>
                    <section className={sectionLargeMarginStyle}>
                        <PropertyDetailDetails property={property} variables={variables} />
                    </section>

                    <section className={sectionLargeMarginStyle} data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.INVESTMENT_LOCATION_SECTION}>
                        {props.shouldMapBeRendered ? (
                            <PropertyDetailLocation
                                changeModalTab={props.changeModalTab}
                                offer={offer}
                                geoAreaCoordinates={offer.geo_area.coordinates}
                                geoPointCoordinates={offer.geo_point.coordinates}
                            />
                        ) : null}
                    </section>

                    <section className={sectionSmallMarginStyle}>
                        <PropertyDetailDescription property={property} variables={variables} />
                    </section>

                    {showVendorTrustCertificate && (
                        <section className={sectionSmallMarginStyle}>
                            <VendorTrustCertificate vendor={offer.vendor} />
                        </section>
                    )}

                    <section className={sectionSmallMarginStyle}>
                        <TimeStamp lastUpdateDate={offer.stats?.last_update ?? ""} publishDate={publishDate} showBorders />
                    </section>
                </TwoColumnGridMain>
            </TwoColumnGridHolder>
        </>
    );
});

const wideContentStyle = css({
    mb: 5
});

const headlineStyle = css({
    mb: 2
});

const sectionLargeMarginStyle = css({
    mb: 5
});

const sectionSmallMarginStyle = css({
    mb: 2
});

const navBarWrapperStyle = css({
    "@media (min-width: 768px) and (max-width: 1310px)": {
        pt: "60px" // other bars height
    }
});

const navBarContentStyle = css({
    "@media (min-width: 768px) and (max-width: 1310px)": {
        borderTop: "1px solid var(--colors-gray-400)"
    }
});
import React from "react";
import {css} from "@panda/css";

import {WideContent} from "@pg-design/grid-module";
import {Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {ColumnList} from "../../atoms/ColumnList";
import {ColumnListItem} from "../../atoms/ColumnListItem";
import {TimeStamp} from "../../atoms/TimeStamp";
import {TwoColumnGridHolder, TwoColumnGridMain} from "../../atoms/TwoColumnGrid";
import {useGetOfferListQuery} from "../../offer/api/get_offer_list";
import {OfferBox} from "../../offer/components/offer_box/OfferBox";
import {OfferModal} from "../../offer/components/offer_modal/OfferModal";
import {showOfferModal} from "../../offer/state/offer_modals_slice";
import {offerBoxTracking} from "../../offer/tracking/offer_box_gtm_events";
import {IOfferListOffer} from "../../offer/types/IOfferListOffer";
import {OfferModalActiveTab} from "../../offer/types/OfferModalActiveTab";
import {IOfferModalName} from "../../offer/types/OfferModalName";
import {getDetailPageRelatedOffersPayload} from "../../offer/utils/get_detail_page_related_offers_payload";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {getRequestStateFromRtkQuery} from "../../store/utils/get_request_state_from_rtk_query";
import {useGTMEcommerceOfferListViewTracking} from "../../tracking/hooks/use_gtm_ecommerce_offer_list_view_tracking";
import {FilterPresets, useUserPreferencesFilters} from "../../user/hooks/use_user_preferences_filters";
import {IVariables} from "../../variables/reducers/variables_reducer";
import {VendorTrustCertificate} from "../../vendor/components/VendorTrustCertificate";
import {IPropertyDetail} from "../types/IPropertyDetail";
import {IPropertyDetailOffer} from "../types/IPropertyDetailOffer";
import {PropertyDetailDescription} from "./PropertyDetailDescription";
import {PropertyDetailDetails} from "./PropertyDetailDetails";
import {PropertyDetailLocation} from "./PropertyDetailLocation";
import {PropertyNavBar} from "./PropertyNavBar";

interface IProps {
    offer: IPropertyDetailOffer;
    property: IPropertyDetail;
    relatedOffers: IOfferListOffer[];
    variables: IVariables | null;
    changeModalTab: (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection) => void;
    showPriceAndContactButtons: boolean;
    shouldMapBeRendered?: boolean;
}

const modalName: IOfferModalName = "mainOfferModal";

export const PropertyDetailLimited: React.FC<IProps> = React.memo((props) => {
    const {property, offer, variables, relatedOffers} = props;

    const dispatch = useAppDispatch();
    const modalOpened = useAppSelector((state) => state.offerModals[modalName].isOpen);
    const userPreferencesFilters = useUserPreferencesFilters(FilterPresets.WithoutRegion);

    const {
        data: relatedOffersResponse,
        isSuccess,
        isUninitialized,
        isLoading,
        isFetching,
        isError
    } = useGetOfferListQuery(
        getDetailPageRelatedOffersPayload({
            page: 1,
            offer
        }),
        {skip: !modalOpened}
    );

    const showVendorTrustCertificate = !!offer.vendor.awards_count;
    const publishDate = !offer.configuration.hide_publish_date && offer.configuration.publish_date ? offer.configuration.publish_date : null;

    const closestInvestmentsTitle = "Najbliższe inwestycje";
    const {containerId} = useGTMEcommerceOfferListViewTracking({
        offers: relatedOffers,
        listId: "najbliższe_inwestycje",
        listName: closestInvestmentsTitle,
        useVisibilityTracking: true
    });

    return (
        <>
            <PropertyNavBar
                className={navBarWrapperStyle}
                navBarContentStyle={navBarContentStyle}
                offer={property.offer}
                onButtonClick={props.changeModalTab}
            />

            {relatedOffers.length > 0 && (
                <>
                    <WideContent className={wideContentStyle}>
                        <Text as="h2" className={headlineStyle} variant="headline_4">
                            {closestInvestmentsTitle}
                        </Text>

                        <ColumnList id={containerId} data-id={containerId}>
                            {relatedOffers.map((offer, index) => (
                                <ColumnListItem key={offer.id}>
                                    <OfferBox
                                        offer={offer}
                                        onShowOfferDetailsBtnClick={() => {
                                            dispatch(showOfferModal({modalName, offerId: offer.id, offer}));
                                        }}
                                        index={index}
                                        trackingMeta={{
                                            listName: closestInvestmentsTitle,
                                            listId: "najbliższe_inwestycje",
                                            source: "additional",
                                            tileEvents: {
                                                imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
                                                buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
                                                linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
                                                logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
                                            }
                                        }}
                                    />
                                </ColumnListItem>
                            ))}
                        </ColumnList>
                    </WideContent>
                    <OfferModal
                        modalName={modalName}
                        offersQuery={{
                            isInitialized: !isUninitialized,
                            isSuccess,
                            isLoading: isLoading || isFetching,
                            isError,
                            requestState: getRequestStateFromRtkQuery({isSuccess, isLoading, isFetching, isError}),
                            data: relatedOffersResponse ? relatedOffersResponse.results : [],
                            count: relatedOffersResponse ? relatedOffersResponse.count : 0,
                            page: relatedOffersResponse ? relatedOffersResponse.page : 1,
                            pageSize: relatedOffersResponse ? relatedOffersResponse.page_size : 3
                        }}
                        userPreferencePropertyFilters={userPreferencesFilters}
                    />
                </>
            )}

            <TwoColumnGridHolder>
                <TwoColumnGridMain>
                    <section className={sectionLargeMarginStyle}>
                        <PropertyDetailDetails property={property} variables={variables} />
                    </section>

                    <section className={sectionLargeMarginStyle} data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.INVESTMENT_LOCATION_SECTION}>
                        {props.shouldMapBeRendered ? (
                            <PropertyDetailLocation
                                changeModalTab={props.changeModalTab}
                                offer={offer}
                                geoAreaCoordinates={offer.geo_area.coordinates}
                                geoPointCoordinates={offer.geo_point.coordinates}
                            />
                        ) : null}
                    </section>

                    <section className={sectionSmallMarginStyle}>
                        <PropertyDetailDescription property={property} variables={variables} />
                    </section>

                    {showVendorTrustCertificate && (
                        <section className={sectionSmallMarginStyle}>
                            <VendorTrustCertificate vendor={offer.vendor} />
                        </section>
                    )}

                    <section className={sectionSmallMarginStyle}>
                        <TimeStamp lastUpdateDate={offer.stats?.last_update ?? ""} publishDate={publishDate} showBorders />
                    </section>
                </TwoColumnGridMain>
            </TwoColumnGridHolder>
        </>
    );
});

const wideContentStyle = css({
    mb: 5
});

const headlineStyle = css({
    mb: 2
});

const sectionLargeMarginStyle = css({
    mb: 5
});

const sectionSmallMarginStyle = css({
    mb: 2
});

const navBarWrapperStyle = css({
    "@media (min-width: 768px) and (max-width: 1310px)": {
        pt: "60px" // other bars height
    }
});

const navBarContentStyle = css({
    "@media (min-width: 768px) and (max-width: 1310px)": {
        borderTop: "1px solid var(--colors-gray-400)"
    }
});
